import React, { useContext, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PencilIcon } from '@heroicons/react/24/outline';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { AppContext } from '../AppContext.js';
import { compactCurrencyFormat, currencyFormat } from '../helpers.js';
import {
	KEY_METRIC_ACCENTS,
	KEY_METRIC_ITEMS,
	keyMetricIdFromPathname,
	shouldShowKeyMetricsNav,
	useCachedKeyMetricAmounts,
	useKeyMetricAmounts,
} from '../hooks/useKeyMetricAmounts.js';

const SHELL =
	'group/metric relative flex flex-1 sm:flex-none items-center gap-1.5 bg-gray-900 rounded-md shadow-sm border border-transparent border-l-[3px] px-1.5 py-1.5 sm:px-2 cursor-pointer hover:bg-gray-800 transition-all duration-150 text-left';

const AMOUNT = 'whitespace-nowrap text-left text-xs sm:text-sm font-semibold tabular-nums text-white leading-none';

function MetricAmount({ children, pending }) {
	if (pending) return <Skeleton width={52} height={12} className="mt-0.5" baseColor="#374151" highlightColor="#4b5563" />;
	return <p className={AMOUNT}>{children}</p>;
}

function resolveWebsite(company, search) {
	try {
		const params = new URLSearchParams(search?.replace(/^\?/, '') || '');
		const fromUrl = params.get('website');
		if (fromUrl && String(fromUrl).trim()) return String(fromUrl).trim().toLowerCase();
	} catch {
		/* ignore */
	}
	const domain = company?.fields?.Domain;
	if (domain && String(domain).trim()) {
		return String(domain)
			.trim()
			.toLowerCase()
			.replace(/^https?:\/\//, '')
			.replace(/^www\./, '')
			.split('/')[0];
	}
	return '';
}

/**
 * Sticky bottom key-metrics nav — lives in Layout so it persists across earnings routes.
 * Prefers /api/key-metrics (company cache); falls back to local compute while API loads.
 */
export default function KeyMetricsNav({ sidebarCollapsed = true, getNavSearch = () => '' }) {
	const location = useLocation();
	const navigate = useNavigate();
	const { getToken } = useAuth();
	const {
		company,
		grants,
		matchedDiscounts,
		totalTechSavings,
		loading,
		grantLoading,
	} = useContext(AppContext);

	const website = useMemo(
		() => resolveWebsite(company, location.search),
		[company, location.search]
	);

	const show = shouldShowKeyMetricsNav(location.pathname);
	const selectedId = keyMetricIdFromPathname(location.pathname);

	const local = useKeyMetricAmounts({
		company,
		grants,
		matchedDiscounts,
		techSavings: totalTechSavings,
		loading,
		grantLoading,
	});

	const cached = useCachedKeyMetricAmounts({
		website,
		getToken,
		company,
		enabled: show && Boolean(website),
		activeMetricId: selectedId,
		live: local,
	});

	if (!show) return null;

	// Prefer company/API cache for instant paint; blend local when cache still pending.
	const amounts = {
		loading: website ? cached.loading && cached.grants == null && !cached.financing : local.loading,
		financing: website && (cached.financing || !cached.loading) ? cached.financing : local.financing || cached.financing,
		software:
			website && !cached.software.pending
				? cached.software
				: local.software?.pending
					? local.software
					: cached.software?.amount
						? cached.software
						: local.software,
		grants: website
			? cached.grants != null
				? cached.grants
				: local.grants
			: local.grants,
		ai: website && (cached.ai || !cached.loading) ? cached.ai : local.ai || cached.ai,
		earnings:
			website && cached.earnings != null
				? cached.earnings
				: local.earnings ?? cached.earnings,
		referrals: website && !cached.loading ? cached.referrals : local.referrals || cached.referrals,
	};

	const globalPending = Boolean(website ? cached.loading && !cached.financing && !cached.ai : local.loading);

	const metricValue = (id) => {
		if (id === 'earnings') {
			const soft = amounts.software?.amount || 0;
			const grantsReady = amounts.grants != null;
			const softPending = Boolean(amounts.software?.pending);
			const totalFromParts =
				(Number(amounts.financing) || 0) +
				(Number(soft) || 0) +
				(Number(amounts.grants) || 0) +
				(Number(amounts.ai) || 0);
			if (amounts.earnings != null && (!grantsReady || softPending)) return amounts.earnings;
			return totalFromParts || amounts.earnings || 0;
		}
		if (id === 'financing') return amounts.financing;
		if (id === 'software') return amounts.software?.amount || 0;
		if (id === 'grants') return amounts.grants || 0;
		if (id === 'referrals') return amounts.referrals;
		return amounts.ai;
	};

	const amountFor = (id) => {
		const perYear = id !== 'financing' && id !== 'referrals';
		const suffix = perYear ? (
			<span className="font-normal text-white/50 text-[10px]">/yr</span>
		) : null;

		if (id === 'earnings') {
			const soft = amounts.software?.amount || 0;
			const grantsReady = amounts.grants != null;
			const softPending = Boolean(amounts.software?.pending);
			const hasAnything =
				amounts.earnings != null ||
				amounts.financing ||
				amounts.ai ||
				(soft && !softPending) ||
				grantsReady;
			return (
				<MetricAmount pending={globalPending && !hasAnything}>
					{compactCurrencyFormat(metricValue(id))}
					{suffix}
					{!grantsReady && hasAnything ? (
						<span className="ml-0.5 font-normal text-white/50" title="Grants still updating">
							*
						</span>
					) : null}
				</MetricAmount>
			);
		}
		if (id === 'financing') {
			return (
				<MetricAmount pending={globalPending || amounts.loading}>
					{compactCurrencyFormat(amounts.financing)}
				</MetricAmount>
			);
		}
		if (id === 'software') {
			const soft = amounts.software || { pending: true, amount: 0 };
			return (
				<MetricAmount pending={globalPending || soft.pending}>
					{compactCurrencyFormat(soft.amount)}
					{suffix}
					{soft.isEstimate ? (
						<span className="ml-0.5 font-normal text-white/50" title="Estimated from software spend">
							*
						</span>
					) : null}
				</MetricAmount>
			);
		}
		if (id === 'grants') {
			return (
				<MetricAmount pending={amounts.grants == null || globalPending}>
					{compactCurrencyFormat(amounts.grants || 0)}
					{suffix}
				</MetricAmount>
			);
		}
		if (id === 'referrals') {
			return (
				<MetricAmount pending={globalPending}>
					{compactCurrencyFormat(amounts.referrals)}
				</MetricAmount>
			);
		}
		return (
			<MetricAmount pending={globalPending || amounts.loading}>
				{compactCurrencyFormat(amounts.ai)}
				{suffix}
			</MetricAmount>
		);
	};

	const go = (path) => {
		if (path === '/company-earnings-estimate' && company?.id) {
			navigate(`/company-earnings-estimate?company=${encodeURIComponent(company.id)}`);
			return;
		}
		navigate(`${path}${getNavSearch()}`);
	};

	return (
		<nav
			aria-label="Key metrics"
			className={`pointer-events-none fixed bottom-2 z-30 flex justify-center left-1.5 right-1.5 lg:right-3 ${
				sidebarCollapsed
					? 'lg:left-[4.75rem]'
					: 'lg:left-[16.75rem]'
			}`}
		>
			<div className="pointer-events-auto w-full sm:w-fit max-w-full rounded-lg border border-gray-200/80 bg-[#F5F0EA]/90 px-1 py-1 shadow-md backdrop-blur-sm sm:px-1.5 sm:py-1.5">
				<div className="flex items-stretch gap-1 sm:gap-1.5" role="tablist">
					{KEY_METRIC_ITEMS.map((metric, index) => {
						const selected = selectedId === metric.id;
						const accent = KEY_METRIC_ACCENTS[metric.id];
						const Icon = metric.icon;
						const value = metricValue(metric.id);
						const perYear = metric.id !== 'financing' && metric.id !== 'referrals';
						const popoverPos =
							index === 0
								? 'left-0'
								: index === KEY_METRIC_ITEMS.length - 1
									? 'right-0'
									: 'left-1/2 -translate-x-1/2';
						return (
							<button
								key={metric.id}
								type="button"
								role="tab"
								aria-selected={selected}
								aria-label={`${metric.label}: ${compactCurrencyFormat(value)}${perYear ? ' per year' : ''}`}
								onClick={() => go(metric.path)}
								className={`${SHELL}${selected ? ' bg-gray-800 ring-1 ring-white/20' : ''}`}
								style={{
									borderLeftColor: accent,
								}}
							>
								<span
									aria-hidden="true"
									className={`pointer-events-none absolute bottom-full z-10 mb-2 w-max max-w-[14rem] rounded-lg bg-gray-800 px-2.5 py-2 text-left opacity-0 shadow-lg transition-opacity duration-150 group-hover/metric:opacity-100 group-focus-visible/metric:opacity-100 ${popoverPos}`}
								>
									<span className="block text-xs font-medium text-white">{metric.label}</span>
									<span className="mt-0.5 block text-xs tabular-nums text-white/70">
										{currencyFormat(value)}
										{perYear ? '/yr' : ''}
									</span>
									<span className="mt-1 block text-[11px] font-normal leading-snug text-white/60">
										{metric.tooltip}
									</span>
								</span>
								{Icon ? (
									<span
										className={`flex shrink-0 items-center justify-center ${
											selected ? 'text-white' : 'text-white/90'
										}`}
										aria-hidden="true"
									>
										<Icon className="h-5 w-5" strokeWidth={1.75} />
									</span>
								) : null}
								{amountFor(metric.id)}
							</button>
						);
					})}
					<button
						type="button"
						onClick={() => go('/company-info-section')}
						className="inline-flex shrink-0 items-center justify-center rounded-md border border-white/10 bg-gray-900 px-1.5 sm:px-2 text-white/60 hover:bg-gray-800 hover:text-white transition-colors"
						title="Edit company info"
						aria-label="Edit company info"
					>
						<PencilIcon className="h-3.5 w-3.5" />
					</button>
				</div>
			</div>
		</nav>
	);
}
